/**
 * 🐋 Checkout Page - Cetalingua FM
 * 
 * Handles signup flow for:
 * - Free sticker program
 * - Advertiser, Station, Decoder, Researcher and bundles (prices: ../portal/plans.json)
 * 
 * UPDATES:
 * ✅ Added latitude/longitude fields for station plans (required)
 * ✅ Auto-assigns stationId via transaction counter (starts at 500)
 * ✅ Creates stations/{stationId} doc at registration
 * ✅ Writes stationId back to users/{uid}
 * 
 * STAGE 3 (29 Sep 2026): paid plans now go to Stripe. The account (role, station ID, station record) is created by
 * the server (Cloud Function registerAccount) — the browser may not set a role. Plans and prices come from
 * ../portal/plans.json (same file as Stripe). No setup fee. initialPlan = 'free' (sticker program) or
 * { planId, period } (e.g. { planId: 'station_advertiser', period: 'm3' }); old keys ('monthly', 'station', …) still work.
 */

import React, { useState, useEffect } from 'react';
import PasswordInput from '../common/PasswordInput';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../../firebase-config';
import portalPlans from '../portal/plans.json';
import { INCLUDES, PRODUCT_NAMES } from '../portal/PortalPlans';
import './CheckoutPage.css';
import { preloadRecaptcha, getRecaptchaToken } from '../../utils/recaptcha';

// Free sticker program (the only plan not sold through Stripe)
const PLANS = {
  'free': {
    name: 'Sticker Partner (Free)',
    price: 0,
    period: '3 months',
    role: 'advertiser',
    subscriptionType: 'free',
    features: ['Company profile', 'Create listings', 'Species targeting', 'Basic app access'],
  },
  // paid plans: from ../portal/plans.json (see resolvePlan)
};

// old plan keys from the landing pages -> new plan + billing period
const OLD_KEYS = {
  monthly: { planId: 'advertiser', period: 'm1' }, '3-month': { planId: 'advertiser', period: 'm3' },
  '6-month': { planId: 'advertiser', period: 'm6' }, '12-month': { planId: 'advertiser', period: 'm12' },
  station: { planId: 'station', period: 'm1' }, combined: { planId: 'station_advertiser', period: 'm1' },
};
const accountTypeFor = (products) => {
  const st = products.includes('station'), ad = products.includes('advertiser');
  return st && ad ? 'station_advertiser' : st ? 'station' : ad ? 'advertiser' : 'analyst';
};
/** the plan shown on this page, in the shape the page already used (name, price, period, role, …) */
export const resolvePlan = (initialPlan) => {
  if (initialPlan === 'free') return { ...PLANS.free, accountType: 'advertiser' };
  const sel = typeof initialPlan === 'object' ? initialPlan : OLD_KEYS[initialPlan] || OLD_KEYS.monthly;
  const p = portalPlans.plans.find((x) => x.id === sel.planId) || portalPlans.plans.find((x) => x.id === 'advertiser');
  const period = portalPlans.periods[sel.period] ? sel.period : 'm1';
  const months = portalPlans.periods[period].months;
  const shown = p.products.filter((x) => !(x === 'decoder' && p.products.includes('researcher')));
  const accountType = accountTypeFor(p.products);
  return {
    planId: p.id, periodKey: period, accountType,
    name: months === 1 ? p.name : `${p.name} — ${months} months`,
    price: p.prices[period],
    period: months === 1 ? '/month' : ` every ${months} months`,
    role: accountType, subscriptionType: 'paid', setupFee: null,
    trialDays: p.trialDays || 0,
    features: shown.length === 1 ? INCLUDES[shown[0]] : shown.map((x) => `Everything in ${PRODUCT_NAMES[x]}`),
  };
};

const CheckoutPage = ({ initialPlan, onBack, onNavigate }) => {
  const [step, setStep] = useState(1); // 1: Account, 2: Company Info, 3: Payment/Confirm
  useEffect(() => { preloadRecaptcha(); }, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    // Account
    email: '',
    password: '',
    confirmPassword: '',
    // Company
    companyName: '',
    phone: '',
    website: '',
    city: '',
    state: '',
    country: '',
    description: '',
    // ✅ NEW: Station coordinates
    latitude: '',
    longitude: '',
    // Sticker program (1 Oct 2026): where to mail the free stickers / flyers
    shipName: '',
    shipStreet: '',
    shipStreet2: '',
    shipPostalCode: '',
    shipSameAsCompany: true,
    shipCity: '',
    shipState: '',
    shipCountry: '',
  });

  const plan = resolvePlan(initialPlan);
  const isStationPlan = plan.role === 'station' || plan.role === 'station_advertiser';

  // "19.64, -156.0" pasted into Latitude (as copied from Google Maps / OpenStreetMap) -> both fields
  const pasteCoords = (e) => {
    const m = (e.clipboardData.getData('text') || '').match(/(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)/);
    if (m) {
      e.preventDefault();
      setFormData((prev) => ({ ...prev, latitude: m[1], longitude: m[2] }));
    }
  };
  // stations are often set up on site: take the browser's location (the user is asked to allow it)
  const useMyLocation = () => {
    if (!navigator.geolocation) { setError('This browser cannot share its location — please type the coordinates.'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setFormData((prev) => ({ ...prev, latitude: pos.coords.latitude.toFixed(5), longitude: pos.coords.longitude.toFixed(5) })),
      () => setError('Location not available — please type the coordinates.'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateStep1 = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const isStickerPlan = plan.subscriptionType === 'free';
  const shippingAddress = () => ({
    name: formData.shipName || formData.companyName,
    street: formData.shipStreet, street2: formData.shipStreet2, postalCode: formData.shipPostalCode,
    city: formData.shipSameAsCompany ? formData.city : formData.shipCity,
    state: formData.shipSameAsCompany ? formData.state : formData.shipState,
    country: formData.shipSameAsCompany ? formData.country : formData.shipCountry,
  });

  const validateStep2 = () => {
    if (!formData.companyName.trim()) {
      setError('Company name is required');
      return false;
    }

    // Sticker program: a mailing address for the free promo materials
    if (isStickerPlan) {
      const a = shippingAddress();
      if (!a.street.trim() || !a.postalCode.trim() || !(a.city || '').trim() || !(a.country || '').trim()) {
        setError('Please give the full mailing address for your free stickers (street, city, ZIP / postal code, country).');
        return false;
      }
    }

    // ✅ Station plans require coordinates
    if (isStationPlan) {
      const lat = parseFloat(formData.latitude);
      const lng = parseFloat(formData.longitude);

      if (!formData.latitude || isNaN(lat)) {
        setError('Latitude is required for station registration');
        return false;
      }
      if (!formData.longitude || isNaN(lng)) {
        setError('Longitude is required for station registration');
        return false;
      }
      if (lat < -90 || lat > 90) {
        setError('Latitude must be between -90 and 90');
        return false;
      }
      if (lng < -180 || lng > 180) {
        setError('Longitude must be between -180 and 180');
        return false;
      }
    }

    return true;
  };

  const handleNext = () => {
    setError('');
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      // 0. invisible security check (reCAPTCHA v3); the server checks this token in registerAccount
      const recaptchaToken = await getRecaptchaToken('signup');

      // 1. Firebase login
      await createUserWithEmailAndPassword(auth, formData.email, formData.password);

      // 2. The server creates the account: role, station ID + station record (station plans)
      const register = httpsCallable(getFunctions(), 'registerAccount');
      await register({
        recaptchaToken,
        accountType: plan.accountType,
        profile: {
          companyName: formData.companyName, phone: formData.phone, website: formData.website,
          description: formData.description,
          location: { city: formData.city, state: formData.state, country: formData.country },
        },
        station: isStationPlan ? { lat: formData.latitude, lng: formData.longitude } : null,
      });

      // 2b. Sticker program: the mailing request goes straight to the admin's "📦 Promo requests" list
      if (isStickerPlan) {
        try {
          await httpsCallable(getFunctions(), 'requestPromoMaterials')({ address: shippingAddress() });
        } catch (e) {
          console.warn('Promo request not saved (can be sent again in the Free Program tab):', e.message);
        }
      }

      // 3. Paid plan -> Stripe's checkout page. Free sticker program -> done (proof photo in the dashboard)
      if (plan.price > 0) {
        const start = httpsCallable(getFunctions(), 'createCheckoutSession');
        const res = await start({ planId: plan.planId, period: plan.periodKey });
        window.location.href = res.data.url;
        return;
      }
      setSuccess(true);
    } catch (err) {
      console.error('Signup error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Please sign in instead.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else {
        setError(err.message || 'Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (success) {
    const isStation = plan.role === 'station' || plan.role === 'station_advertiser';
    
    return (
      <div className="checkout-container">
        <div className="checkout-card success-card">
          <div className="success-icon">🎉</div>
          <h2>Account Created!</h2>
          
          {plan.subscriptionType === 'free' ? (
            <>
              <p>Welcome to Cetalingua FM! Your account is ready.</p>
              <div className="success-next-steps">
                <h3>Next Steps:</h3>
                <ol>
                  <li>Set up your company profile</li>
                  <li>Wait for your free stickers — we mail them to the address you gave</li>
                  <li>Display stickers and upload a proof photo (Free Program tab)</li>
                  <li>Get 3 months of free advertising!</li>
                </ol>
              </div>
            </>
          ) : isStation ? (
            <>
              <p>Welcome to Cetalingua FM! Your {plan.name} account is being set up.</p>
              {plan.price > 0 && (
                <div className="success-payment-note">
                  <p>💳 Payment processing will be completed shortly.</p>
                </div>
              )}
              <div className="success-next-steps">
                <h3>Next Steps for Your Station:</h3>
                <ol>
                  <li>Complete your station profile (name, location, description)</li>
                  <li>Watch the setup tutorials in your dashboard</li>
                  <li>Set up your AudioMoth/HydroMoth or existing hydrophone</li>
                  <li>Upload your first recordings</li>
                  <li>Your station goes live on the global map!</li>
                </ol>
              </div>
              <div className="success-tutorials-note">
                <span className="tutorials-icon">📹</span>
                <p>
                  Your station dashboard includes video and PDF tutorials for setting up 
                  AudioMoth/HydroMoth recorders. We're here to help if you have questions!
                </p>
              </div>
              {plan.role === 'station_advertiser' && (
                <div className="success-combo-note">
                  <p>🐋 <strong>Combined Package:</strong> You also have full advertiser access to promote your tours!</p>
                </div>
              )}
            </>
          ) : (
            <>
              <p>Welcome to Cetalingua FM! Your {plan.name} account is being set up.</p>
              {plan.price > 0 && (
                <div className="success-payment-note">
                  <p>💳 Payment processing will be completed shortly.</p>
                </div>
              )}
              <div className="success-next-steps">
                <h3>Next Steps:</h3>
                <ol>
                  <li>Complete your company profile</li>
                  <li>Add photos and select whale species</li>
                  <li>Create your first listing</li>
                </ol>
              </div>
            </>
          )}

          <button 
            className="success-button"
            onClick={() => window.location.reload()}
          >
            Go to Dashboard →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <div className="checkout-card">
        {/* Header */}
        <div className="checkout-header">
          <button className="back-btn" onClick={onBack}>← Back</button>
          <h1>🐋 Create Your Account</h1>
        </div>

        {/* Plan Summary */}
        <div className="plan-summary">
          <div className="plan-summary-name">{plan.name}</div>
          <div className="plan-summary-price">
            {plan.price === 0 ? (
              <span className="free-label">FREE</span>
            ) : (
              <>
                <span className="price">${plan.price}</span>
                <span className="period">{plan.period}</span>
              </>
            )}
          </div>
          {plan.setupFee && (
            <div className="plan-summary-setup">+ ${plan.setupFee} one-time setup fee</div>
          )}
        </div>

        {/* Progress Steps */}
        <div className="progress-steps">
          <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <span className="step-num">1</span>
            <span className="step-label">Account</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <span className="step-num">2</span>
            <span className="step-label">Company</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
            <span className="step-num">3</span>
            <span className="step-label">Confirm</span>
          </div>
        </div>

        {/* Error */}
        {error && <div className="error-message">{error}</div>}

        {/* Step 1: Account */}
        {step === 1 && (
          <div className="checkout-step">
            <h2>Account Details</h2>
            <p className="step-desc">Create your login credentials</p>
            
            <div className="form-group">
              <label>Email Address *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your@email.com"
                autoComplete="email"
              />
            </div>
            
            <div className="form-group">
              <label>Password *</label>
              <PasswordInput
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="At least 6 characters"
                autoComplete="new-password"
              />
            </div>
            
            <div className="form-group">
              <label>Confirm Password *</label>
              <PasswordInput
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirm your password"
                autoComplete="new-password"
              />
            </div>
            
            <button className="next-btn" onClick={handleNext}>
              Continue →
            </button>
          </div>
        )}

        {/* Step 2: Company Info */}
        {step === 2 && (
          <div className="checkout-step">
            <h2>{isStationPlan ? 'Station Information' : 'Company Information'}</h2>
            <p className="step-desc">
              {isStationPlan 
                ? 'Tell us about your station and location'
                : 'Tell us about your whale watching business'
              }
            </p>
            
            <div className="form-group">
              <label>{isStationPlan ? 'Station / organisation name *' : 'Name / organisation *'}</label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleInputChange}
                placeholder={isStationPlan ? 'Monterey Bay Whale Station' : 'Pacific Whale Watch Tours'}
              />
            </div>
            
            <div className="form-group">
              <label>Phone (Optional)</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="+1 (555) 123-4567"
              />
            </div>
            
            <div className="form-group">
              <label>Website (Optional)</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                placeholder="https://yourcompany.com"
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Monterey"
                />
              </div>
              <div className="form-group">
                <label>State/Province</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  placeholder="CA"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>Country</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                placeholder="USA"
              />
            </div>

            {/* ✅ NEW: Station coordinates (required for station plans) */}
            {isStationPlan && (
              <>
                <div className="station-coords-section">
                  <h3>📍 Station Coordinates *</h3>
                  <p className="coords-help">
                    Enter the GPS coordinates where your hydrophone is located. 
                    This places your station on the global map. 
                    Find them on <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer">Google Maps</a> or <a href="https://www.openstreetmap.org" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> (no login):
                    right-click the spot (long-press on a phone) → the coordinates appear at the top → click to copy →
                    paste both numbers into Latitude (e.g. <code>19.6400, -156.0000</code>) and they are split for you.
                  </p>
                  <button type="button" className="back-step-btn" style={{ marginBottom: 12 }} onClick={useMyLocation}>
                    📍 Use my current location
                  </button>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Latitude * <span className="hint">(-90 to 90)</span></label>
                      <input
                        type="number"
                        name="latitude"
                        value={formData.latitude}
                        onChange={handleInputChange}
                        onPaste={pasteCoords}
                        placeholder="e.g., 36.6002"
                        step="any"
                        min="-90"
                        max="90"
                      />
                    </div>
                    <div className="form-group">
                      <label>Longitude * <span className="hint">(-180 to 180)</span></label>
                      <input
                        type="number"
                        name="longitude"
                        value={formData.longitude}
                        onChange={handleInputChange}
                        placeholder="e.g., -121.8947"
                        step="any"
                        min="-180"
                        max="180"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {/* Sticker program: mailing address for the free promo materials (no need to email us) */}
            {isStickerPlan && (
              <div className="station-coords-section">
                <h3>📦 Where should we mail your free stickers?</h3>
                <p className="coords-help">We send the promotional materials free of charge — no need to email us.</p>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                  <input type="checkbox" checked={formData.shipSameAsCompany}
                         onChange={(e) => setFormData((p) => ({ ...p, shipSameAsCompany: e.target.checked }))} />
                  Same city / state / country as the company above
                </label>
                <div className="form-group">
                  <label>Name / attention (optional)</label>
                  <input type="text" name="shipName" value={formData.shipName} onChange={handleInputChange} placeholder={formData.companyName || 'Company or person'} />
                </div>
                <div className="form-group">
                  <label>Street address *</label>
                  <input type="text" name="shipStreet" value={formData.shipStreet} onChange={handleInputChange} placeholder="1 Harbor Road" />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Apt / suite / dock (optional)</label>
                    <input type="text" name="shipStreet2" value={formData.shipStreet2} onChange={handleInputChange} />
                  </div>
                  <div className="form-group">
                    <label>ZIP / postal code *</label>
                    <input type="text" name="shipPostalCode" value={formData.shipPostalCode} onChange={handleInputChange} />
                  </div>
                </div>
                {!formData.shipSameAsCompany && (
                  <div className="form-row">
                    <div className="form-group"><label>City *</label>
                      <input type="text" name="shipCity" value={formData.shipCity} onChange={handleInputChange} /></div>
                    <div className="form-group"><label>State / province</label>
                      <input type="text" name="shipState" value={formData.shipState} onChange={handleInputChange} /></div>
                    <div className="form-group"><label>Country *</label>
                      <input type="text" name="shipCountry" value={formData.shipCountry} onChange={handleInputChange} /></div>
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label>Brief Description (Optional)</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder={isStationPlan 
                  ? 'Tell listeners about your station location and the marine life in your area...'
                  : 'Tell whale enthusiasts about your tours...'
                }
                rows={3}
              />
            </div>

            {isStationPlan && (
              <div className="station-info-note">
                <span className="info-icon">📡</span>
                <p>
                  After signup, you'll have access to video and PDF tutorials for setting up 
                  AudioMoth/HydroMoth recorders and uploading your first recordings.
                </p>
              </div>
            )}
            
            <div className="button-row">
              <button className="back-step-btn" onClick={() => setStep(1)}>
                ← Back
              </button>
              <button className="next-btn" onClick={handleNext}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <div className="checkout-step">
            <h2>Confirm & Create Account</h2>
            <p className="step-desc">Review your information</p>
            
            <div className="confirm-section">
              <h3>Account</h3>
              <p>{formData.email}</p>
            </div>
            
            <div className="confirm-section">
              <h3>Company</h3>
              <p className="company-name">{formData.companyName}</p>
              {(formData.city || formData.country) && (
                <p className="company-location">
                  {[formData.city, formData.state, formData.country].filter(Boolean).join(', ')}
                </p>
              )}
              {/* ✅ Show coordinates in confirmation for station plans */}
              {isStationPlan && formData.latitude && formData.longitude && (
                <p className="company-location">
                  📍 {formData.latitude}, {formData.longitude}
                </p>
              )}
            </div>
            
            <div className="confirm-section">
              <h3>Plan</h3>
              <p className="plan-name">{plan.name}</p>
              <p className="plan-cost">
                {plan.price === 0 ? (
                  'Free (with sticker proof)'
                ) : (
                  <>
                    ${plan.price}{plan.period}
                    {plan.setupFee && ` + $${plan.setupFee} setup`}
                  </>
                )}
              </p>
            </div>

            {plan.subscriptionType === 'free' && (
              <div className="free-note">
                <p>📋 After signup:</p>
                <ol>
                  <li>We mail your free stickers to the address you gave</li>
                  <li>Display them and upload a proof photo (Free Program tab)</li>
                  <li>Once approved, you get 3 months free! (once per company)</li>
                </ol>
              </div>
            )}

            {plan.price > 0 && (
              <div className="payment-note">
                <p>💳 After creating your account you go to Stripe's secure payment page.{plan.trialDays ? ` ${plan.trialDays}-day free trial first.` : ''}</p>
              </div>
            )}

            <div className="button-row">
              <button className="back-step-btn" onClick={() => setStep(2)}>
                ← Back
              </button>
              <button 
                className="submit-btn" 
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Creating Account...' : (
                  plan.price > 0 ? 'Create Account & Pay' : 'Create Account'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="checkout-footer">
          <p>
            By creating an account, you agree to our{' '}
            <a href="#" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('terms'); }}>
              Terms of Service
            </a>
            {' '}and{' '}
            <a href="#" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('privacy'); }}>
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
